import { Direction, Locale } from "@common/constans";
import { CabinClass, FlightPoint, IOriginDestination } from "@common/models";
import Alert from "@components/general/Alert";
import Divider from "@components/general/Divider";
import Icon from "@components/general/Icon";
import { Culture } from "@core/index";
import { IAppState } from "@misc/State";
import Translate from "@service/Translate";
import { formatToDateTime, getAirport, minToMinHour } from "@utilize/Format";
import { FC, Fragment } from "react";
import { useSelector } from "react-redux";
import FlightDetailsResponsive from "./FlightDetailsResponsive";

interface IFlightDetailsProps {
    flights: IOriginDestination[];
}

const FlightDetails: FC<IFlightDetailsProps> = ({ flights: originDestinations }) => {
    const { tr } = Translate;
    const { lang } = Culture.getLocale();
    const IrLocale = Culture.getLocale(Locale.fa);
    const { fleets } = useSelector((state: IAppState) => ({ fleets: state.fleets }));

    const renderStopSection = (od: IOriginDestination) => {
        const dateTimeString = lang === IrLocale.lang ? "l d M Y" : "l, d M Y";
        if (od.stops && od.stops.length) {
            return (
                <div className="flight-route-stops-content">
                    <div className="flight-route-icon">
                        <Icon name="icon-airplane" className="flight-route-icon-airplane" />
                        <div className="flight-route-icon-info">
                            <Divider mode="perforation" orientation="horizontal" />
                        </div>
                        <div className="flight-route-icon-dot" />
                    </div>
                    {
                        od.stops.map((stop, index) => {
                            return (
                                <Fragment key={index}>
                                    <div className="stop-dot">
                                        <div className="flight-route-city-name">{getAirport(stop.airport.code).cityName}</div>
                                        <div className="flight-route-airport-name">{getAirport(stop.airport.code).airportName} ({stop.airport.code})</div>
                                        <div className="stop-duration">{stop.duration ? minToMinHour(stop.duration) : ""}</div>
                                        {
                                            stop.startTime ?
                                                <div className="stop-time arr-time">
                                                    <div className="flight-route-time">{formatToDateTime(stop.startTime, { format: "H:i" })}</div>
                                                    <div className="flight-route-date">{formatToDateTime(stop.startTime, { format: dateTimeString })}</div>
                                                </div>
                                                : ""
                                        }
                                        {
                                            <div className="stop-flight-number">
                                                <span>{tr("flight_no")}:</span>
                                                <div className="b flight-number en">
                                                    <span className="flight-owner">{od.flightSegments[0].owner}</span>
                                                    <b>{od.flightSegments[0].flightNumber?.substring(2)}</b>
                                                </div>
                                            </div>
                                        }
                                        {
                                            stop.endTime ?
                                                <div className="stop-time dep-time">
                                                    <div className="flight-route-time">{formatToDateTime(stop.endTime, { format: "H:i" })}</div>
                                                    <div className="flight-route-date">{formatToDateTime(stop.endTime, { format: dateTimeString })}</div>
                                                </div>
                                                : ""
                                        }
                                    </div>
                                    <div className="flight-route-icon">
                                        <Icon name="icon-airplane" className="flight-route-icon-airplane" />
                                        <div className="flight-route-icon-info">
                                            <Divider mode="perforation" orientation="horizontal" />
                                        </div>
                                        <div className="flight-route-icon-dot" />
                                    </div>
                                </Fragment>
                            )
                        })
                    }
                </div>
            )
        } else {
            return (
                <div className="flight-route-icon">
                    <Icon name="icon-airplane" className="flight-route-icon-airplane" />
                    <div className="flight-route-icon-info">
                        <div className="flight-route-details-item">
                            <span>{tr("flight_no")}:</span>
                            <div className="b flight-number en">
                                <span className="flight-owner">{od.flightSegments[0].owner}</span>
                                <b>{od.flightSegments[0].flightNumber?.substring(2)}</b>
                            </div>
                        </div>
                        <Divider mode="perforation" orientation="horizontal" />
                        <div className="flight-route-details-item">
                            <span>{tr("flight_duration")}:</span>
                            <b>{minToMinHour(od.flightSegments[0].journeyDuration || 0)}</b>
                        </div>
                    </div>
                    <div className="flight-route-icon-dot" />
                </div>
            );
        }
    }

    const flightDetailsItems = originDestinations.map((od, index) => {
        const dateTimeString = lang === IrLocale.lang ? "l d M Y" : "l, d M Y";

        return (
            <div key={index} className="divider-box-section">
                <div className="flight-route-title">
                    <span>{originDestinations.length === 2 && originDestinations[0].origin?.code === originDestinations[1].destination?.code ? tr(index ? "flight_return" : "flight_departure") : od.code} </span>
                </div>
                <div className={od.stops && od.stops.length ? "itinerary-flight-stop-route" : "itinerary-flight-route"}>
                    <div className="flight-route-decription">
                        <div className="flight-route-content">
                            <div className="flight-route-origin-content">
                                <div className="flight-route-city-name">{getAirport(od.origin.code).cityName}</div>
                                <div className="flight-route-airport-name">{getAirport(od.origin.code).airportName} ({od.origin.code})</div>
                                <div className="flight-route-terminal">{new FlightPoint(od.origin).getDefaultTerminal(Direction.Departure, od.destination?.code || "")?.displayText}</div>
                                {
                                    od.departureDateTime ?
                                        <>
                                            <div className="flight-route-time">{formatToDateTime(od.departureDateTime, { format: "H:i" })}</div>
                                            <div className="flight-route-date">{formatToDateTime(od.departureDateTime, { format: dateTimeString })}</div>
                                        </> : ""
                                }
                            </div>
                            {renderStopSection(od)}
                            <div className="flight-route-destination-content">
                                <div className="flight-route-city-name">{getAirport(od.destination.code).cityName}</div>
                                <div className="flight-route-airport-name">{getAirport(od.destination.code).airportName} ({od.destination.code})</div>
                                <div className="flight-route-terminal">{new FlightPoint(od.destination).getDefaultTerminal(Direction.Arrival, od.origin?.code || "")?.displayText}</div>
                                {
                                    od.arrivalDateTime ?
                                        <>
                                            <div className="flight-route-time">{formatToDateTime(od.arrivalDateTime, { format: "H:i" })}</div>
                                            <div className="flight-route-date">{formatToDateTime(od.arrivalDateTime, { format: dateTimeString })}</div>
                                        </> : ""
                                }
                            </div>
                        </div>
                    </div>
                    <div className="flight-route-details">
                        <div className="flight-route-details-item">{tr("calss_of_service")}: <span className="b">{tr(`cabin_class_${CabinClass[od.classOfService].toLowerCase()}`)}</span></div>
                        <div className="flight-route-details-item">{tr("fare_class")}: <span className="b">{od.fareRules && od.fareRules[0] ? od.fareRules[0].fareClass : ""}</span></div>
                        <div className="flight-route-details-item">{tr("aircraft_model")}: <span className="b">{fleets.find(f => f.itineraryCode?.toLowerCase() === od?.flightSegments[0].equipmentModelInfo?.toLowerCase())?.displayText}</span></div>
                        <div className="flight-route-details-item">{tr("free_baggage")}: <span className="b">{od.fareRules && od.fareRules[0] ? tr("units_kg_value", od.fareRules[0].freeBaggage) : ""}</span></div>
                    </div>
                </div>
                {originDestinations.length === (index + 1) ? "" : <div className="divider-line"></div>}
            </div>
        );
    });

    return (
        <div className="itinerary-flight-details">
            <Alert
                type="defult"
                className="itinerary-time-alert"
                variant="none"
                icon="icon-information-outline"
                seperate
            >
                {tr("all_time_in_local")}
            </Alert>
            <div className="flight-details-header">
                {tr("flight_details")}
            </div>
            <div className="flight-details-body">
                <FlightDetailsResponsive flights={originDestinations} />
                <div className="divider-box">
                    {flightDetailsItems}
                </div>
            </div>
        </div>
    )
}

export default FlightDetails;